import { FeConstants } from 'constants/FeConstants';
import { ListType } from 'crono-fe-common/types/list';
import useSearchLists from 'hooks/services/lists/useSearchLists';
import useSearchUsers, {
  useSearchUsersInfiniteScroll,
} from 'hooks/services/user/useSearchUsers';
import { useInfiniteScrollSentinel } from 'hooks/useInfiniteScrollSentinel';
import { useAuth } from 'context/auth';
import Role from 'crono-fe-common/types/enums/role';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fullName } from 'crono-fe-common/utils';
import { TeamsUsersSelectView } from './TeamsUsersSelectView';

interface BaseProps {
  triggerClassName?: string;
  /** Shown before the label on the default trigger. */
  triggerIcon?: React.ReactNode;
  popoverClassName?: string;
  children?: React.ReactNode;
  defaultTeamId?: number;
  onTeamClick?: (teamId: number | undefined) => void;
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether the picker is open. The picker opens and closes itself when this is
   * left out; pass it only where the host has to be able to close it — a drawer
   * row, say, whose popup would otherwise outlive the scroll that took its
   * trigger off screen.
   */
  open?: boolean;
  placeholder?: string;
  collisionBoundary?: Element | null;
  side?: 'top' | 'bottom' | 'left' | 'right';
  size?: 'sm' | 'md';
  forceTriggerWidth?: boolean;
  /** Shows the selection but can't be opened. */
  disabled?: boolean;
  /**
   * By default, navigating back from a team's user list (or from "All users")
   * clears the current selection. Set this to keep the selection intact — useful
   * when users are accumulated across several teams.
   */
  preserveSelectionOnNavigateBack?: boolean;
}

interface MultipleProps extends BaseProps {
  multiple: true;
  selectedUsersIds: number[];
  onChangeSelectedUsersIds: (ids: number[]) => void;
  selectedTeamsIds?: number[];
  onChangeSelectedTeamsIds?: (ids: number[]) => void;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  selectedUsersIds: number | undefined;
  onChangeSelectedUsersIds: (id: number | undefined) => void;
  /**
   * Name of the selected user, used to label the trigger when they fall outside
   * the loaded pages of the paginated users list. Entities that carry their
   * owner's name (prospect, account, opportunity) should pass it.
   */
  selectedUserName?: string | null;
}

export type TeamsUsersSelectProps = MultipleProps | SingleProps;

const LAST_TEAM_KEY = 'teamsUsersSelect.lastSelectedTeamId';

export const TeamsUsersSelect = (props: TeamsUsersSelectProps) => {
  const {
    triggerClassName,
    triggerIcon,
    popoverClassName,
    children,
    multiple,
    selectedUsersIds,
    onChangeSelectedUsersIds,
    defaultTeamId,
    onTeamClick,
    onOpenChange,
    open: openProp,
    placeholder,
    collisionBoundary,
    side,
    size = 'md',
    forceTriggerWidth = false,
    disabled = false,
    preserveSelectionOnNavigateBack = false,
  } = props;

  const { user } = useAuth();
  const isManager = user?.userRoles?.includes(Role.MANAGER) ?? false;

  const { data: allUsersWithTeamsData } = useSearchUsers(
    {
      limit: FeConstants.allUsersWithTeamsLimit,
      offset: 0,
      sortType: 'Name',
      withTeams: true,
      text: user?.firstName ?? undefined,
    },
    !multiple && !isManager && !!user?.id,
  );

  const singleModeDefaultTeamId = useMemo(() => {
    if (multiple || isManager || !user?.id) return undefined;
    const currentUser = allUsersWithTeamsData?.data?.data?.find(
      (u) => u.id === user.id,
    );
    const teams = currentUser?.teams ?? [];
    return teams.length === 1 ? teams[0].id : undefined;
  }, [multiple, isManager, allUsersWithTeamsData, user?.id]);

  const [selfOpen, setSelfOpen] = useState(false);
  const popoverOpen = openProp ?? selfOpen;
  const [selectedTeamId, setSelectedTeamId] = useState<number>();
  const [allUsersSelected, setAllUsersSelected] = useState(false);

  const showUsersList = !!selectedTeamId || allUsersSelected;

  const { data: teamsListData } = useSearchLists({
    type: ListType.User,
    sortType: 'Name',
  });

  const teamsList = teamsListData?.data.data ?? [];

  const selectedTeam = teamsList.find((t) => t.id === selectedTeamId);

  const [usersSearch, setUsersSearch] = useState('');

  const {
    data: usersData,
    isLoading: isLoadingUsers,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSearchUsersInfiniteScroll({
    limit: FeConstants.usersPageLimit,
    text: usersSearch || undefined,
    sortType: 'Name',
    withTeams: true,
    listId: selectedTeamId,
    currentUserFirst: true,
  });

  const users = useMemo(
    () => usersData?.pages.flatMap((page) => page.data.data ?? []) ?? [],
    [usersData],
  );

  const usersScrollRef = useRef<HTMLDivElement>(null);

  const sentinelRef = useInfiniteScrollSentinel({
    root: usersScrollRef,
    enabled: showUsersList,
    rootMargin: '0px 0px 80px 0px',
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const [cachedSelectedUser, setCachedSelectedUser] = useState<
    (typeof users)[0] | undefined
  >(undefined);

  // Optimistic: immediately set on click, cleared once selectedUsersIds catches up
  const [pendingSelectedUser, setPendingSelectedUser] = useState<
    (typeof users)[0] | undefined
  >(undefined);

  useEffect(() => {
    if (multiple || !selectedUsersIds) return;
    const found = users.find((u) => u.id === selectedUsersIds);
    if (found) setCachedSelectedUser(found);
  }, [users, selectedUsersIds, multiple]);

  // Clear pending once the parent prop has caught up
  useEffect(() => {
    if (pendingSelectedUser && selectedUsersIds === pendingSelectedUser.id) {
      setPendingSelectedUser(undefined);
    }
  }, [selectedUsersIds, pendingSelectedUser]);

  const selectedUser = !multiple
    ? (pendingSelectedUser ??
      (cachedSelectedUser?.id === selectedUsersIds
        ? cachedSelectedUser
        : undefined))
    : undefined;

  const selectedUserFallbackName =
    !multiple && selectedUsersIds !== undefined
      ? (props.selectedUserName ?? undefined)
      : undefined;

  const selectedUserLabel = selectedUser
    ? fullName(selectedUser.firstName, selectedUser.lastName)
    : selectedUserFallbackName;

  function handleClickUser(userId: number) {
    if (multiple) return;
    const clicked = users.find((u) => u.id === userId);
    if (clicked) setPendingSelectedUser(clicked);
    onChangeSelectedUsersIds(userId);
    setSelfOpen(false);
    resetNavigation();
  }

  function handleToggleUser(userId: number) {
    if (!multiple) return;
    if (props.selectedUsersIds.includes(userId)) {
      props.onChangeSelectedUsersIds(
        props.selectedUsersIds.filter((id) => id !== userId),
      );
    } else {
      props.onChangeSelectedUsersIds([...props.selectedUsersIds, userId]);
    }
  }

  function handleClickTeam(teamId: number) {
    setSelectedTeamId(teamId);
    localStorage.setItem(LAST_TEAM_KEY, String(teamId));
    setUsersSearch('');
    onTeamClick?.(teamId);
    if (multiple) {
      props.onChangeSelectedTeamsIds?.([]);
    }
  }

  const selectedCount = multiple
    ? props.selectedUsersIds.length + (props.selectedTeamsIds?.length ?? 0)
    : 0;

  function handleClickHeader() {
    if (selectedTeam) {
      setSelectedTeamId(undefined);
      localStorage.removeItem(LAST_TEAM_KEY);
      onTeamClick?.(undefined);
      if (multiple && !preserveSelectionOnNavigateBack) {
        props.onChangeSelectedUsersIds([]);
      }
    } else if (allUsersSelected) {
      setAllUsersSelected(false);
      if (multiple && !preserveSelectionOnNavigateBack) {
        props.onChangeSelectedUsersIds([]);
      }
    } else {
      setAllUsersSelected(true);
    }
  }

  function handleToggleAllTeamUsers(checked: boolean) {
    if (!multiple) return;

    const idsInList = users.map((u) => u.id);
    const selectedOutsideList = props.selectedUsersIds.filter(
      (id) => !idsInList.includes(id),
    );

    props.onChangeSelectedUsersIds(
      checked ? [...selectedOutsideList, ...idsInList] : selectedOutsideList,
    );
  }

  function getTriggerLabel() {
    if (multiple) {
      return selectedCount > 0 ? `(${selectedCount}) selected` : placeholder;
    }
    return selectedUserLabel ?? placeholder;
  }

  const triggerLabel = getTriggerLabel();
  const hasSelection = multiple ? selectedCount > 0 : !!selectedUserLabel;

  const resetNavigation = useCallback(() => {
    setSelectedTeamId(undefined);
    setAllUsersSelected(false);
    setUsersSearch('');
  }, []);

  // A close the host drove never reaches handleOpenChange — the popover only
  // reports the ones it made itself — so the drill-down is put back here.
  useEffect(() => {
    if (openProp === false) resetNavigation();
  }, [openProp, resetNavigation]);

  function handleOpenChange(isOpen: boolean) {
    setSelfOpen(isOpen);
    onOpenChange?.(isOpen);

    if (!isOpen) {
      resetNavigation();
    } else if (teamsList.length === 0) {
      setAllUsersSelected(true);
    } else {
      const storedId = Number(localStorage.getItem(LAST_TEAM_KEY)) || undefined;
      const lastTeamId =
        storedId && teamsList.some((t) => t.id === storedId)
          ? storedId
          : undefined;
      if (storedId && !lastTeamId) {
        localStorage.removeItem(LAST_TEAM_KEY);
      }

      if (lastTeamId) {
        setSelectedTeamId(lastTeamId);
      } else if (defaultTeamId) {
        const teamsSelected = multiple
          ? (props.selectedTeamsIds?.length ?? 0) > 0
          : false;
        if (!teamsSelected) {
          setSelectedTeamId(defaultTeamId);
        }
      } else if (!multiple) {
        const teamToOpen = singleModeDefaultTeamId;
        if (teamToOpen) {
          setSelectedTeamId(teamToOpen);
        } else {
          setAllUsersSelected(true);
        }
      }
    }
  }

  if (multiple) {
    return (
      <TeamsUsersSelectView
        size={size}
        triggerClassName={triggerClassName}
        triggerIcon={triggerIcon}
        popoverClassName={popoverClassName}
        children={children}
        collisionBoundary={collisionBoundary}
        side={side}
        forceTriggerWidth={forceTriggerWidth}
        disabled={disabled}
        popoverOpen={popoverOpen}
        onOpenChange={handleOpenChange}
        triggerLabel={triggerLabel}
        hasSelection={hasSelection}
        teamsList={teamsList}
        selectedTeamId={selectedTeamId}
        allUsersSelected={allUsersSelected}
        isLoadingUsers={isLoadingUsers}
        users={users}
        usersSearch={usersSearch}
        currentUserId={user?.id}
        onSearchChange={setUsersSearch}
        usersScrollRef={usersScrollRef}
        sentinelRef={sentinelRef}
        onClickTeam={handleClickTeam}
        onClickHeader={handleClickHeader}
        multiple
        selectedUsersIds={props.selectedUsersIds}
        onToggleUser={handleToggleUser}
        onToggleAllTeamUsers={handleToggleAllTeamUsers}
      />
    );
  }

  return (
    <TeamsUsersSelectView
      size={size}
      triggerClassName={triggerClassName}
      triggerIcon={triggerIcon}
      popoverClassName={popoverClassName}
      children={children}
      collisionBoundary={collisionBoundary}
      side={side}
      forceTriggerWidth={forceTriggerWidth}
      disabled={disabled}
      popoverOpen={popoverOpen}
      onOpenChange={handleOpenChange}
      triggerLabel={triggerLabel}
      hasSelection={hasSelection}
      teamsList={teamsList}
      selectedTeamId={selectedTeamId}
      allUsersSelected={allUsersSelected}
      isLoadingUsers={isLoadingUsers}
      users={users}
      usersSearch={usersSearch}
      currentUserId={user?.id}
      onSearchChange={setUsersSearch}
      usersScrollRef={usersScrollRef}
      sentinelRef={sentinelRef}
      onClickTeam={handleClickTeam}
      onClickHeader={handleClickHeader}
      multiple={false}
      selectedUsersIds={selectedUsersIds}
      onClickUser={handleClickUser}
    />
  );
};
